import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import { ApiService } from './api.service';
import { TaskStatus } from '../components/tasks/TaskStatus';

export interface ProjectHealthLevel {
  min_score: number;
  label: string;
  colour: string;
}

export interface ProjectHealthConfig {
  version: 1;
  hours_per_workday: number;
  due_date_weight: number;
  overdue_task_weight: number;
  unassigned_task_weight: number;
  estimated_time_weight: number;
  awaiting_missing_info_weight: number;
  due_date_smoothing_days: number;
  project_due_min_days_away: number;
  task_due_min_days_away: number;
  team_member_assigned_days_away: number;
  health_score_sensitivity: number;
  levels: ProjectHealthLevel[];
}

export interface ProjectHealthResult {
  score: number;
  level: ProjectHealthLevel;
  breakdown: {
    remaining_hours: number;
    days_until_due: number;
    available_hours: number;
    due_date_pressure: number;
    estimated_time_pressure: number;
    outstanding_task_count: number;
    overdue_task_count: number;
    staffing_relevant_task_count: number;
    unassigned_task_count: number;
    is_awaiting_missing_info: boolean;
  };
}

// Project status code for "Awaiting Missing Info" (see SolveProjectStatus in
// components/projects/table/table.component.ts, the only other place this status is decoded -
// there's no shared ProjectStatus enum for this like TaskStatus has for tasks).
export const PROJECT_STATUS_AWAITING_MISSING_INFO = 21;

// Computes a 0-100 "Project Health" score for active projects from four independently-weighted
// pressure signals: how close the project's own due date is, how many outstanding tasks are already
// overdue, how many soon-due-or-overdue outstanding tasks still have nobody assigned, and how much
// estimated work is left versus what a normal workday could get through before the due date. The firm tunes
// each factor's weight (a weight of "None" removes that factor from the score entirely) and the score
// bands (levels) from Manage Firm > Projects > Manage Health Scoring - see health-settings.component.ts.
// Backed by firms.project_health_json (a single JSON blob column) via GET/PUT firms/project_health
// (see Firms.cs) - GetConfig/SaveConfig are the only two places that touch it, so any future storage
// change stays contained to this file.
@Injectable({
  providedIn: 'root'
})
export class ProjectHealthService {

  // Templates call Evaluate() synchronously (same as the existing SolveProjectStatus(project) pattern
  // elsewhere), so the config is cached here and kept fresh by GetConfig() - starts as the default so
  // nothing breaks before the async load (kicked off in the constructor) resolves.
  private config: ProjectHealthConfig = this.GetDefaultConfig();

  constructor(private apiService: ApiService) {
    this.GetConfig();
  }

  IsProjectActive(project: any): boolean {
    if (!project) return false;
    // Not gated on !project.paused - a project awaiting missing info (status 21) gets paused = true
    // alongside that status change, and it still needs to reach CalculateHealth for
    // awaiting_missing_info_weight to ever have an effect (that's the whole point of the factor).
    return project.status == 0 || project.status == 2
      || project.status == PROJECT_STATUS_AWAITING_MISSING_INFO || (project.status == -1 && project.progress > 0);
  }

  GetDefaultConfig(): ProjectHealthConfig {
    return {
      version: 1,
      hours_per_workday: 8,
      due_date_weight: 0.5,
      overdue_task_weight: 2,
      // On the gentler 0-2 scale (see unassignedTaskLevels in health-settings.component.ts) - this
      // factor counts ALL outstanding tasks regardless of due date, so it's routinely nonzero on
      // perfectly healthy, far-from-due projects.
      unassigned_task_weight: 0.25,
      // Estimated time remaining is off by default until this gets revisited - see the note on
      // estimated_time_pressure in CalculateHealth.
      estimated_time_weight: 0,
      // On the same 0/1/2/4/8 scale as overdue_task_weight - defaults to "Low" rather than matching
      // overdue_task_weight's "Medium", since a project can sit in this status for a legitimate
      // reason for a while and shouldn't tank the score as hard as an actually-overdue task.
      awaiting_missing_info_weight: 1,
      due_date_smoothing_days: 0,
      // 0 = no grace window (the pressure below is active as soon as there's any due date at all,
      // same as before these settings existed) - set above 0 to keep that factor "perfect" until
      // within that many days of the due date.
      project_due_min_days_away: 0,
      task_due_min_days_away: 0,
      // Only tasks due within this many days count toward Factor 3 - a task due far off doesn't
      // matter yet, so being unassigned isn't a real risk. 0 ignores this setting entirely (every
      // outstanding task counts, regardless of due date).
      team_member_assigned_days_away: 14,
      health_score_sensitivity: 0.25,
      levels: [
        { min_score: 60, label: 'Healthy', colour: '#2ecc71' },
        { min_score: 25, label: 'Watch', colour: '#f1c40f' },
        { min_score: 18, label: 'Warning', colour: '#e67e22' },
        { min_score: 15, label: 'At-Risk', colour: '#ff8080' },
        { min_score: 0, label: 'Critical', colour: '#8b0000' },
      ],
    };
  }

  async GetConfig(): Promise<ProjectHealthConfig> {
    try {
      const data: any = await this.apiService.get('firms/project_health');
      const raw = data?.success ? data.data?.[0]?.project_health_json : null;
      this.config = raw ? { ...this.GetDefaultConfig(), ...JSON.parse(raw) } : this.GetDefaultConfig();
    } catch {
      this.config = this.GetDefaultConfig();
    }
    return this.config;
  }

  // Admin-only server-side (see Firms.cs's UpdateProjectHealthConfig) - the Manage Health Scoring
  // button that reaches this is itself disabled for non-admins, this is just defense in depth.
  async SaveConfig(config: ProjectHealthConfig): Promise<{ success: boolean; message?: string }> {
    const data: any = await this.apiService.put('firms/project_health', { project_health_json: JSON.stringify(config) });
    if (data?.success) this.config = config;
    return { success: !!data?.success, message: data?.message };
  }

  GetLevelForScore(score: number, levels: ProjectHealthLevel[]): ProjectHealthLevel {
    const fallback = this.GetDefaultConfig().levels;
    const sorted = [...(levels?.length ? levels : fallback)].sort((a, b) => b.min_score - a.min_score);
    return sorted.find(l => score >= l.min_score) || sorted[sorted.length - 1];
  }

  CalculateHealth(project: any, config: ProjectHealthConfig, now: Date = new Date()): ProjectHealthResult {
    const remaining_hours = Math.max((project?.estimated_time_remaining || 0) / 60, 0);

    // No due date set - treat as no deadline pressure (effectively the "500 days out" case) rather
    // than as urgent-because-unknown.
    const days_until_due = project?.internal_end_date
      ? dayjs(project.internal_end_date).endOf('day').diff(dayjs(now), 'hour', true) / 24
      : 3650;

    // due_date_smoothing_days keeps both date-based pressures below from spiking toward infinity right
    // at (or past) the due date - floored at a tiny fraction of a day so a firm that sets smoothing to
    // 0 (the default) doesn't get an outright divide-by-zero (Infinity/NaN, which would poison
    // urgency_final below even for a factor whose weight is 0) once a project is actually overdue.
    const smoothed_days_until_due = Math.max(Math.max(days_until_due, 0) + config.due_date_smoothing_days, 0.001);

    // Factor 1 - Project End Date: pressure purely from how close the project's own due date is,
    // independent of how much work is actually left. Perfect (zero pressure) until the project comes
    // within project_due_min_days_away days of its due date - 0 (the default) means no grace window,
    // so the pressure is active for any due date, matching this factor's behaviour before the setting
    // existed.
    const due_date_pressure = (config.project_due_min_days_away > 0 && days_until_due > config.project_due_min_days_away)
      ? 0
      : 1 / smoothed_days_until_due;

    // Factor 4 - Estimated Time Remaining: remaining work compared to what a normal workday could get
    // through before the due date. Off by default (estimated_time_weight = 0).
    const available_hours = smoothed_days_until_due * config.hours_per_workday;
    const estimated_time_pressure = remaining_hours / available_hours;

    const outstanding_tasks = (project?.tasks || []).filter((t: any) => t?.status !== TaskStatus.completed);

    // Factor 2 - Task Dates: outstanding tasks whose own due date has already passed - or, once
    // task_due_min_days_away is set above 0, tasks that have come within that many days of their due
    // date (the same "perfect until within N days" grace window as Factor 1). 0 (the default) keeps
    // this to just already-overdue tasks, matching this factor's behaviour before the setting existed.
    const overdue_tasks = outstanding_tasks.filter((t: any) => {
      if (!t?.due_date) return false;
      if (config.task_due_min_days_away > 0) {
        const task_days_until_due = dayjs(t.due_date).endOf('day').diff(dayjs(now), 'hour', true) / 24;
        return task_days_until_due <= config.task_due_min_days_away;
      }
      return dayjs(t.due_date).isBefore(dayjs(now), 'day');
    });
    const overdue_ratio = outstanding_tasks.length > 0 ? overdue_tasks.length / outstanding_tasks.length : 0;

    // Factor 3 - Team Member Assigned: of the outstanding tasks that are actually close enough to
    // matter (due within team_member_assigned_days_away days, or already overdue), how many still
    // have nobody assigned - a task due far off isn't a staffing risk yet. 0 (the default off state
    // for this gate would be "always relevant"; the actual default is 14) ignores this narrowing
    // entirely and counts every outstanding task regardless of due date.
    const staffing_relevant_tasks = config.team_member_assigned_days_away > 0
      ? outstanding_tasks.filter((t: any) => {
          if (!t?.due_date) return false;
          const task_days_until_due = dayjs(t.due_date).endOf('day').diff(dayjs(now), 'hour', true) / 24;
          return task_days_until_due <= config.team_member_assigned_days_away;
        })
      : outstanding_tasks;
    const unassigned_tasks = staffing_relevant_tasks.filter((t: any) => !t?.assigned_member_guid);
    const unassigned_ratio = staffing_relevant_tasks.length > 0 ? unassigned_tasks.length / staffing_relevant_tasks.length : 0;

    // Factor 5 - Awaiting Missing Info: the project itself is stalled waiting on the client to
    // provide something (status 21 - see SolveProjectStatus in table.component.ts). Binary, not a
    // ratio like the task-based factors, so its pressure is just 0 or 1.
    const is_awaiting_missing_info = project?.status == PROJECT_STATUS_AWAITING_MISSING_INFO;
    const awaiting_missing_info_pressure = is_awaiting_missing_info ? 1 : 0;

    // Each factor is scaled by its own firm-tunable weight before being summed, so an admin decides
    // how much each one should move the score - setting a weight to 0 (None) removes that factor from
    // the score entirely.
    const urgency_final =
      (config.due_date_weight * due_date_pressure) +
      (config.overdue_task_weight * overdue_ratio) +
      (config.unassigned_task_weight * unassigned_ratio) +
      (config.estimated_time_weight * estimated_time_pressure) +
      (config.awaiting_missing_info_weight * awaiting_missing_info_pressure);

    const score = Math.min(Math.max(100 / (1 + urgency_final / config.health_score_sensitivity), 0), 100);

    return {
      score,
      level: this.GetLevelForScore(score, config.levels),
      breakdown: {
        remaining_hours, days_until_due, available_hours, due_date_pressure, estimated_time_pressure,
        outstanding_task_count: outstanding_tasks.length, overdue_task_count: overdue_tasks.length,
        staffing_relevant_task_count: staffing_relevant_tasks.length, unassigned_task_count: unassigned_tasks.length,
        is_awaiting_missing_info,
      },
    };
  }

  // A project whose start date hasn't arrived yet has no meaningful health to compute (there's no
  // elapsed time/progress to judge against, and rendering a score for hundreds of not-yet-started
  // projects at once is wasted work) - only start dates today or in the past count as "started".
  // Missing internal_start_date entirely doesn't block computing health.
  HasProjectStarted(project: any, now: Date = new Date()): boolean {
    if (!project?.internal_start_date) return true;
    return !dayjs(project.internal_start_date).isAfter(dayjs(now), 'day');
  }

  // Cached config, synchronously - for template bindings that just need the current levels (e.g. a
  // health-filter dropdown) without computing a score.
  GetCachedConfig(): ProjectHealthConfig {
    return this.config;
  }

  // What the table/details templates actually call - null for non-active or not-yet-started projects
  // (see IsProjectActive/HasProjectStarted).
  Evaluate(project: any): ProjectHealthResult | null {
    if (!this.IsProjectActive(project)) return null;
    if (!this.HasProjectStarted(project)) return null;
    return this.CalculateHealth(project, this.config);
  }

  // Turns a computed health result into plain-English reasons for why the score came out the way
  // it did, for the hover tooltip on the health badge. Each factor is only listed when its
  // firm-configured weight is > 0 (matching CalculateHealth's "weight 0 = factor removed from
  // scoring") and its underlying condition is actually true. Overdue tasks and unassigned tasks
  // reuse the exact grace windows CalculateHealth itself applies (task_due_min_days_away /
  // team_member_assigned_days_away), so a task the score doesn't currently penalize won't show up
  // here either. The project's own due date pressure is mathematically never exactly zero once
  // there's a due date at all (it just gets tiny), so it falls back to a 14-day "worth mentioning"
  // window for display purposes only when the firm hasn't set project_due_min_days_away - otherwise
  // a project due next year would show "due in 400 days" as if that were driving the score.
  GetHealthReasons(health: ProjectHealthResult, config: ProjectHealthConfig): string[] {
    const b = health.breakdown;
    const reasons: string[] = [];

    if (config.awaiting_missing_info_weight > 0 && b.is_awaiting_missing_info) {
      reasons.push('Project is awaiting missing info from the client');
    }

    if (config.overdue_task_weight > 0 && b.overdue_task_count > 0) {
      reasons.push(`${b.overdue_task_count} uncompleted task${b.overdue_task_count === 1 ? ' is' : 's are'} overdue`);
    }

    if (config.unassigned_task_weight > 0 && b.unassigned_task_count > 0) {
      reasons.push(`${b.unassigned_task_count} task${b.unassigned_task_count === 1 ? '' : 's'} due soon with no team member assigned`);
    }

    // days_until_due is set to 3650 when the project has no due date at all - not a real pressure signal.
    if (config.due_date_weight > 0 && b.days_until_due < 3650) {
      const days_away_threshold = config.project_due_min_days_away > 0 ? config.project_due_min_days_away : 14;
      const rounded_days = Math.round(Math.abs(b.days_until_due));
      if (b.days_until_due < 0) {
        reasons.push(`Project is overdue by ${rounded_days} day${rounded_days === 1 ? '' : 's'}`);
      } else if (b.days_until_due <= days_away_threshold) {
        reasons.push(`Project is due in ${rounded_days} day${rounded_days === 1 ? '' : 's'}`);
      }
    }

    if (config.estimated_time_weight > 0 && b.estimated_time_pressure > 1) {
      reasons.push(`Remaining estimated work (${Math.round(b.remaining_hours)}h) exceeds available time before the due date (${Math.round(b.available_hours)}h)`);
    }

    if (reasons.length === 0) reasons.push('No significant risk factors');

    return reasons;
  }
}
